import { useTranslation } from 'react-i18next';

import { AtExitProvider, RecallAsOfProvider } from './answer-ref-context';
import { ChipEvalIcon } from './eval-chip';
import ImageBlock from './image-block';
import SoundBlock from './sound-block';
import TextBlock from './text-block';

import type { ChipEvaluation } from './eval-chip';
import type { Block } from '@/types/experience';
import type { ExitBlock, ExitContent, FeedbackState } from '@agavi/blocks';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';
import { worstEvaluation } from '@/lib/block-verdict';


/**
 * What Submit did, said once, in the way round.
 *
 * ## The problem this replaces
 *
 * Submit used to change the task silently. Feedback grew inline wherever it belonged — which is the
 * right place for it to LIVE — and a summary bar appeared at the bottom of the list. Neither
 * announced itself. On a phone both landed off-screen; on a desktop the bar arrived below a task
 * you were already scrolled into the middle of. A student pressed a button and, as far as they could
 * tell, nothing happened.
 *
 * The two surfaces were also fighting for the same job. The bar was trying to be the verdict while
 * sitting where only furniture sits, and the inline feedback was trying to be the verdict while
 * being scattered across a page.
 *
 * ## The split
 *
 * They are two different jobs and they get two different surfaces:
 *
 *   - **The verdict interrupts.** How many blocks came out right, wrong and neither — three numbers,
 *     in a dialog that takes the screen. This is the moment SPR got right: the whole task was
 *     evaluated and the student was made to look at the outcome.
 *   - **The detail stays where it happened.** Every fired feedback box remains exactly where it grew,
 *     and the dialog's buttons are the way INTO that detail rather than a summary of it.
 *
 * So the dialog never repeats a single feedback sentence. It says how much there is and offers two
 * ways to go and read it, which is why it can stay three lines long on a task with thirty blocks.
 *
 * ## Why it is a dialog and not a banner
 *
 * A banner is dismissible by ignoring it, and the whole defect was that Submit could be ignored. The
 * dialog also gives the task's inert-and-dimmed state something to be inert BEHIND: with nothing on
 * top, a greyed-out task reads as broken rather than as waiting.
 */

export interface BlockResult {
  blockId: string;
  name: string;
  evaluation: ChipEvaluation;
}

/**
 * One result per BLOCK, not per feedback box.
 *
 * A block can fire several states at once — a wrong answer plus a targeted hint — and counting boxes
 * would report a five-block task as having eight results. The block's worst outcome is the one that
 * describes it: incorrect beats neutral beats correct, because "you got something wrong here" is the
 * fact a student is looking for.
 */
const SEVERITY: Record<string, number> = { incorrect: 3, neutral: 2, correct: 1 };
// `worstEvaluation` shares this ordering — see `@/lib/block-verdict`.

/**
 * DERIVED FROM THE DOM, not from a callback.
 *
 * `FiredFeedback` already sets `data-eval` on every result box, deliberately, so that "the result is
 * legible from outside the block" — Preview's block container reads the same attribute to colour its
 * left rail. Reading it here too means no block gains a prop and all eleven types are covered the day
 * they render feedback, which threading an `onResult` through each of them would not have been.
 */
export function readBlockResults(root: Element, nameFor: (blockId: string) => string): BlockResult[] {
  const worst = new Map<string, ChipEvaluation>();
  for (const box of root.querySelectorAll('[data-eval]')) {
    const container = box.closest('[id^="block-"]');
    if (!container) continue;
    const blockId = container.id.slice('block-'.length);
    const evaluation = (box.getAttribute('data-eval') ?? 'neutral') as ChipEvaluation;
    const current = worst.get(blockId);
    if (!current || (SEVERITY[evaluation] ?? 0) > (SEVERITY[current] ?? 0)) {
      worst.set(blockId, evaluation);
    }
  }
  // Document order, so "the first one you got wrong" means the first one they MET.
  const order = [...root.querySelectorAll('[id^="block-"]')].map(el => el.id.slice('block-'.length));
  return order.filter(id => worst.has(id)).map(id => ({
    blockId: id,
    name: nameFor(id),
    evaluation: worst.get(id)!,
  }));
}

/**
 * THE SAME READING, FROM THE LOG instead of from the screen.
 *
 * A student coming back to a task they already finished is shown the summary again, and by then
 * the DOM holds a freshly mounted task rather than the one that was graded — so the verdict has
 * to be rebuilt from what was recorded.
 *
 * Shares `SEVERITY` with `readBlockResults` rather than restating it. The two readers answer the
 * same question from different sources, and a block's worst outcome drifting between the live
 * verdict and the remembered one would be invisible: both would look right on their own.
 *
 * ORDER COMES FROM THE BLOCK LIST, not from the record, for the reason the DOM reader takes it
 * from document order — "the first one you got wrong" has to mean the first one they met.
 */
export function summarizeRecorded(
  results: Record<string, FeedbackState[]>,
  blocks: { id: string; name?: string }[],
  nameFor: (blockId: string) => string,
): Submission {
  const worst = new Map<string, ChipEvaluation>();
  for (const [blockId, states] of Object.entries(results)) {
    const verdict = worstEvaluation(states);
    if (verdict) worst.set(blockId, verdict);
  }
  return summarize(
    blocks.filter((b) => worst.has(b.id)).map((b) => ({
      blockId: b.id,
      name: nameFor(b.id),
      evaluation: worst.get(b.id)!,
    })),
  );
}

export interface Submission {
  results: BlockResult[];
  correct: number;
  incorrect: number;
  neutral: number;
  /** Where Fix goes. `null` when nothing is wrong, which is also when Fix is not offered. */
  firstIncorrectId: string | null;
  /**
   * Nothing left to put right — so the task is finished and stops accepting answers.
   *
   * NEUTRAL DOES NOT COUNT AS DONE, and that is the whole reason this is not `incorrect === 0`.
   * A positive slot locks to `correct`, so an answered survey block reports correct on its own;
   * anything reporting neutral got there because a teacher deliberately classified that state as
   * neither right nor wrong, and `resolveFlow` treats it as blocking for the same reason. Letting a
   * neutral close the task would overrule that authoring decision from the runtime.
   *
   * SUPERSEDED IN PHASE 2, where this becomes "the flow routed you somewhere" and stops asking
   * about block verdicts at all. Left stated until then so the two definitions never overlap.
   */
  complete: boolean;
}

/**
 * THE SPLIT-BLOCK CARVE-OUT IS GONE, and so is the reason it existed.
 *
 * `summarize` briefly took the split block ids so a pathway choice's neutral would not report
 * the task unfinished. That was the right fix while All Correct was a NEGATIVE test and the
 * routing ladder had a carve-out of its own to stay in step with. Under the positive test there
 * is no carve-out to match: a split block's neutral is not a pass state anywhere, and whether
 * the task is finished is about to stop being a question about block verdicts at all — see
 * `complete`.
 */
export function summarize(results: BlockResult[]): Submission {
  const count = (e: ChipEvaluation) => results.filter(r => r.evaluation === e).length;
  const incorrect = count('incorrect');
  const neutral = count('neutral');
  return {
    results,
    correct: count('correct'),
    incorrect,
    neutral,
    firstIncorrectId: results.find(r => r.evaluation === 'incorrect')?.blockId ?? null,
    complete: incorrect === 0 && neutral === 0 && results.length > 0,
  };
}

/** One count and its icon. Only rendered for a count that actually happened. */
function CountRow({ evaluation, n, label }: { evaluation: ChipEvaluation; n: number; label: string }) {
  const tone = evaluation === 'correct' ? 'bg-ws-success-bg text-ws-success-fg'
    : evaluation === 'incorrect' ? 'bg-ws-error-bg text-ws-error-fg'
    : 'bg-ws-neutral-bg text-ws-neutral-fg';
  return (
    <li className='flex items-center gap-3'>
      <span aria-hidden='true' className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${tone}`}>
        <ChipEvalIcon evaluation={evaluation} />
      </span>
      {/* `tabular-nums` and a reserved box so the figures line up in a column rather than
          wobbling with the glyph widths — they are read down, not across. This only works
          because the LIST is what gets centred: a row that centred itself would carry its own
          column with it. */}
      <span className='w-8 shrink-0 text-right text-lg font-semibold tabular-nums text-ws-text'>{n}</span>
      <span className='min-w-0 text-sm text-ws-muted'>{label}</span>
    </li>
  );
}

/**
 * The verdict. Three numbers and ONE way on.
 *
 * ## Why one button and not two
 *
 * It offered Fix and Review side by side, and the owner's question about them was the finding:
 * *"i'm not sure which one is important versus not."* If the person who designed the product
 * cannot rank the two labels, a student meeting them for the first time, mid-verdict, having
 * just been told they got things wrong, certainly cannot.
 *
 * Laid side by side the choice was not a real one. Fix lands on the first incorrect block with
 * focus in the control — and that block's feedback is right there, so Fix delivers the feedback
 * for the thing you got wrong AND the place to change it. Review lands at the top. In the
 * incorrect case Fix is a superset of Review, positioned; Review's only distinct value is
 * "re-read everything including what I got right", which is the minority intent and is reachable
 * by scrolling, because dismissing reveals the whole task either way.
 *
 * So the two buttons were offering a SCROLL POSITION, and that is not worth a decision at the
 * moment a student is reading a verdict. One button, whose label and destination follow the
 * state — the same rule the bottom bar already follows ("ONE primary, whose label is the state
 * of the task").
 *
 * ## And why Continue is nevertheless a SECOND button
 *
 * The rule above is about two buttons that do the same thing. Continue does not: it leaves the
 * task. Review and Fix both dismiss and stay, so choosing between them was choosing a scroll
 * offset; choosing between staying and going is the only real decision on this dialog.
 *
 * It is here because the dialog is modal: with one button the only way on was to dismiss, find
 * the bar underneath and press Continue there — a student who is done being routed BACK through
 * the work they just finished. Owner: "so you're not forced through the review to continue."
 *
 * IT IS THE PRIMARY, and Review is the quiet one beside it. The emphasis went the other way
 * first, on the reasoning that a verdict with something wrong should send you back to look at
 * it. That reads the dialog as a correction; it is a summary, and the ordinary end of reading a
 * summary is to carry on. Reviewing is the deliberate detour, and a detour does not need to be
 * the loudest thing on the screen to be found.
 *
 * Absent entirely when there is nowhere to go — a flow that keeps the student here has no
 * Continue to offer, and the bottom bar is already disabled in that state. Review is then the
 * only control, and takes the primary back: a dialog whose single way out is styled as the
 * secondary choice is a dialog with no obvious way out.
 *
 * ## What Escape does, and why it differs
 *
 * Escape is the plain dismiss, always to the top, never the jump. "Just close this" should not
 * put a cursor in a field. That does not break the invariant the two-button version protected —
 * neither path is destructive, so there is still no state in which every exit costs something.
 */
export function PreviewSummaryDialog({ submission, exit, exitAt, onReview, onFix, onContinue, endsExperience, returnFocusRef }: {
  submission: Submission;
  /**
   * What the teacher authored on the exit this submission came out of, if anything.
   *
   * THE SYSTEM SAYS HOW MUCH, THE TEACHER SAYS WHAT IT MEANS. The counts above are derived and
   * always there; this is written and usually not. An exit with nothing authored leaves the
   * dialog exactly as it was before any of this existed, which is the right default for the
   * hundred-odd tasks that have none.
   *
   * Here rather than in a second dialog: two summary surfaces in a row is the objection that
   * ruled out a standalone exit screen in the first place.
   */
  exit?: ExitContent;
  /**
   * WHEN this exit fired, so a reference inside it quotes the run as it stood then.
   *
   * Absent while the student is in the present, which is the submission they just made. Set
   * when an earlier visit's summary is being read back — see `RecallAsOfProvider`.
   */
  exitAt?: string;
  onReview: () => void;
  onFix: () => void;
  /**
   * Leave the task, without reading anything first.
   *
   * Absent when the flow has nowhere to send them from here, which is the same fact the bottom
   * bar reads to disable its own primary — a Continue that cannot continue is worse than none.
   */
  onContinue?: () => void;
  /** Whether leaving ENDS the experience, so the label says which. */
  endsExperience?: boolean;
  /** Where focus goes when this closes; see `ModalShell`'s own note. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  // `contained`: this covers the TASK COLUMN, not the page. Preview exists to look like a student's
  // screen, and a student has no accessibility review panel for the verdict to be greying out — the
  // same boundary the bar below it already respects.
  const rows = ([
    ['correct', submission.correct, t('task.submission.correct', { count: submission.correct })],
    ['incorrect', submission.incorrect, t('task.submission.incorrect', { count: submission.incorrect })],
    ['neutral', submission.neutral, t('task.submission.neutral', { count: submission.neutral })],
  ] as const)
    .filter(([, n]) => n > 0)
    .map(([evaluation, n, label]) => (
      <CountRow key={evaluation} evaluation={evaluation} n={n} label={label} />
    ));

  return (
    <ModalShell
      contained
      title={t('task.submission.heading')}
      onClose={onReview}
      maxWidth='max-w-sm'
      /* The bar's primary, for when the capture is worthless -- see `returnFocusRef`. This
         dialog is raised by a SUBMIT and the bar re-renders during it, so by the time the shell
         mounts `document.activeElement` is already the body. */
      returnFocusRef={returnFocusRef}
    >
      {/* `gap-4`, not `gap-5`. Measured on the real dialog, the exit paragraphs were already
          4px apart and what read as loose was the space AROUND them: 36px above the counts, 38
          between the counts and the writing, 21 before the buttons. The verdict is four short
          things and should read as one card, not four. */}
      <div className='flex flex-col gap-4'>
        {/* The counts are the content, so they are a list rather than a sentence: a screen reader
            announces "list, 2 items" and then each count, which is the same shape the eye gets.
            The old bar's single run-on string ("2 correct · 1 incorrect · 0 other") had to be heard
            all the way through before any of it could be held onto.

            ONLY WHAT HAPPENED. The zeros used to be shown on the argument that "0 incorrect" is the
            good news, and in practice they were the opposite: a student who got everything right
            read "2 correct, 0 incorrect, 0 neutral" and had to work out which of the three numbers
            was the verdict. Three numbers is an arithmetic problem; one number is an answer.
            The fallback keeps a wholly empty list impossible, since nothing to report at all would
            be a stranger thing to show than a zero. */}
        {/* THE BLOCK IS CENTRED; THE ROWS ARE NOT, and the difference is the point.
            `mx-auto` on a `w-fit` list centres the set as one object and leaves every row on a
            shared left edge, so the icons stack and the figures stay in a column. Centring each
            row instead does centre each line exactly, and the owner's verdict on seeing it was
            that it reads worse: the icons stagger by the difference in label length. A tidy
            column slightly off the dialog's centre beats three lines that each find their own.
            With one row the two are identical; the choice only shows on a mixed verdict. */}
        <ul className='mx-auto flex w-fit flex-col gap-2'>
          {rows.length > 0 ? rows : (
            <CountRow evaluation='correct' n={0} label={t('task.submission.correct', { count: 0 })} />
          )}
        </ul>
        {/* Under the counts, above the control: the figures are the verdict and this is what it
            MEANS, which is the order a reader wants them in. Separated by a rule rather than by
            spacing alone, because the two halves have different authors — one derived, one
            written — and a reader should be able to tell at a glance. */}
        {exit && exit.blocks.length > 0 && (
          /* `AtExitProvider` as well as the clock: a same-task recall is only resolvable
             BECAUSE this is the exit, and the chip reads that to decide whether to paint
             itself broken. Without it the student's summary would show a red chip for a
             reference that is working perfectly. */
          <AtExitProvider>
          <RecallAsOfProvider at={exitAt ?? null}>
            {/* `gap-1`, not the `gap-3` the rest of the dialog uses: these are paragraphs of one
                piece of writing, and prose carries its own leading. At `gap-3` a two-block exit
                read as two unrelated notices rather than one summary.

                ROOM AROUND THE WRITING, which it had none of. The counts above are three short
                lines and sit comfortably in a dialog that is mostly air; the exit is prose and a
                picture, and both ran to the full width with the rule tight above them — "in the
                summary display in preview, give a little more padding around the image and
                text". The inset is on this block rather than on the dialog, so the counts and
                the buttons keep the measure they were tuned to. */}
            <div className='flex flex-col gap-1 border-t border-ws-border px-2 pb-1 pt-6'>
              {exit.blocks.map((block) => <ExitBlockView key={block.id} block={block} />)}
            </div>
          </RecallAsOfProvider>
          </AtExitProvider>
        )}

        {/* `pt-2` on top of the column's `gap-4`: the buttons are the end of the card and were
            sitting as close to the writing above them as the writing sits to its own rule. */}
        <div className='flex justify-end gap-2 pt-2'>
          {/*
            STAY OR GO DECIDES THIS, and nothing else.
            
            It used to be `firstIncorrectId` — "is anything wrong" — so a task that sent a
            student onward after a wrong answer offered them Fix AND Continue. Fix unlocked the
            task, they changed the answer, and the bar underneath had already become Continue:
            the edit went nowhere and the button carried them on regardless. Reported as exactly
            that mess.
            
            Correctness is the wrong question here for the same reason it is the wrong question
            for the exit itself, which `previewComplete` settled already: *"exit of the page is
            dependent only on what the flow event says, regardless of the state of the blocks."*
            A stay means there is still something to do, so Fix. A go means the task is finished
            with them, however they answered, so Review or Continue — the same two a correct
            answer has always offered, and now what Default, a sensor rung and a special event
            offer too.
            
            `onContinue` IS that signal: the page passes it only when the recorded outcome has
            somewhere to send them. One fact, so the buttons cannot disagree with the bar.
          */}
          {onContinue ? (
            <>
              <Button variant='secondary' onClick={onReview}>{t('task.submission.review')}</Button>
              <Button variant='primary' onClick={onContinue}>
                {t(endsExperience ? 'task.bottomBar.previewFinish' : 'task.bottomBar.previewContinue')}
              </Button>
            </>
          ) : (
            /* Alone, so it carries the primary: it is the only way out of the dialog. */
            <Button variant='primary' onClick={onFix}>{t('task.submission.fix')}</Button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * One authored exit block, as the student reads it.
 *
 * Rendered through the block's OWN component in preview mode, never a hand-rolled copy: an image
 * carries its alt text and a sound its transcript because those components insist on it, and a
 * second renderer here would be a second place for that to lapse.
 *
 * `isPreview` is the whole switch — the same components the authoring modal opens as editors
 * render as content here, which is why a teacher's exit looks in the dialog exactly as it looked
 * while they wrote it.
 */
function ExitBlockView({ block }: { block: ExitBlock }) {
  const noop = () => {};
  const shared = { isPreview: true, onUpdate: noop, onRemove: noop, onDuplicate: noop };
  const asBlock = { ...block, order: 0 } as unknown as Block;

  if (block.type === 'text') return <TextBlock block={asBlock} {...shared} />;
  if (block.type === 'image') return <ImageBlock block={asBlock} experienceId='' taskId='' {...shared} />;
  return <SoundBlock block={asBlock} experienceId='' taskId='' {...shared} />;
}
